/**
 * Owner-only Automation Settings page.
 *
 * Two layers of control:
 *   1. Master switch (top of page) — off = every automation dormant.
 *   2. Per-job switches — each row is one of the ~15 automations in
 *      the KNOWN_JOBS registry on the backend.
 *
 * If the env var `OUTBOUND_AUTOMATION_ENABLED` is unset/false on the
 * server, the page shows a banner explaining that toggles have no
 * effect until an operator flips the env kill-switch. This is the
 * "belt and braces" the Owner asked for after receiving a flood of
 * pilot-phase board-pack + evidence-pack alerts.
 */
import { useEffect, useState } from "react";
import { Navbar } from "@/components/Navbar";
import { Seo } from "@/components/Seo";
import { api } from "@/lib/api";
import { AlertTriangle, Loader2, Power, Save } from "lucide-react";

const CATEGORY_ORDER = ["Reports", "Digests", "Growth", "System"];

export default function Automation() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [error, setError] = useState("");
  const [envEnabled, setEnvEnabled] = useState(true);
  const [master, setMaster] = useState(false);
  const [jobs, setJobs] = useState({}); // { job_id: bool }
  const [registry, setRegistry] = useState([]);

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await api.get("/owner/automation");
      setEnvEnabled(data.settings.env_enabled);
      setMaster(data.settings.master_enabled);
      setJobs(data.settings.jobs || {});
      setRegistry(data.jobs || []);
    } catch (err) {
      setError(err?.response?.data?.detail || err?.message || "Load failed");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    setError("");
    setSaved(false);
    try {
      await api.put("/owner/automation", { master_enabled: master, jobs });
      await load();
      setSaved(true);
      setDirty(false);
      setTimeout(() => setSaved(false), 3500);
    } catch (err) {
      setError(err?.response?.data?.detail || err?.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const setJob = (id, value) => { setJobs((prev) => ({ ...prev, [id]: value })); setDirty(true); setSaved(false); };
  const setAllInCategory = (cat, value) => {
    setJobs((prev) => {
      const next = { ...prev };
      registry.filter((j) => j.category === cat).forEach((j) => { next[j.id] = value; });
      return next;
    });
    setDirty(true);
    setSaved(false);
  };
  const setMasterTracked = (v) => { setMaster(v); setDirty(true); setSaved(false); };

  const grouped = CATEGORY_ORDER.map((cat) => ({
    cat,
    rows: registry.filter((j) => j.category === cat),
  })).filter((g) => g.rows.length > 0);

  return (
    <>
      <Seo title="Automation controls" description="Owner-only automation settings — master and per-job toggles for every scheduled email and notification." path="/automation" />
      <Navbar />
      <main className="mx-auto max-w-4xl px-6 py-10" data-testid="automation-page">
        <p className="text-xs font-semibold uppercase tracking-widest text-ek-purple">Owner controls</p>
        <h1 className="mt-2 font-display text-3xl font-bold text-ek-ink">Automation settings</h1>
        <p className="mt-2 max-w-2xl text-sm text-ek-ink/70">
          Every scheduled report, digest and Owner alert is off by default. Switch on
          only what you (or your customer) actively want delivered. Transactional email
          — password reset, MFA challenge, lead-form acknowledgements — is unaffected.
        </p>

        {!envEnabled && (
          <div className="mt-6 flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50/60 p-4 text-sm text-amber-900" data-testid="automation-env-warning">
            <AlertTriangle size={18} className="mt-0.5 shrink-0 text-amber-700" />
            <div>
              <strong>Environment kill-switch is OFF.</strong> Toggles below are stored but the server will refuse to fire ANY automation until <code className="rounded bg-white/60 px-1 py-0.5 text-xs">OUTBOUND_AUTOMATION_ENABLED=true</code> is set in the environment. This is deliberate: it stops accidental prod-scale mail-outs before a client is onboarded.
            </div>
          </div>
        )}

        {loading ? (
          <div className="mt-8 flex items-center gap-2 text-ek-ink/60"><Loader2 size={16} className="animate-spin" /> Loading…</div>
        ) : (
          <>
            {/* Master switch */}
            <div className="mt-8 flex items-center justify-between rounded-xl border border-ek-purple/25 bg-gradient-to-br from-ek-purple/[0.08] to-transparent p-5">
              <div>
                <p className="text-xs font-semibold uppercase tracking-wider text-ek-purple">Master switch</p>
                <p className="mt-1 text-base font-semibold text-ek-ink">All automations {master ? "enabled" : "paused"}</p>
                <p className="mt-1 text-sm text-ek-ink/70">When off, no scheduled job runs regardless of individual toggles below.</p>
              </div>
              <Toggle checked={master} onChange={setMasterTracked} testid="automation-master" />
            </div>

            {/* Per-job list */}
            <div className="mt-6 space-y-6">
              {grouped.map(({ cat, rows }) => (
                <section key={cat} data-testid={`automation-cat-${cat.toLowerCase()}`}>
                  <div className="mb-2 flex items-center justify-between">
                    <h2 className="text-sm font-semibold uppercase tracking-widest text-ek-ink/60">{cat}</h2>
                    <div className="flex gap-1 text-xs">
                      <button onClick={() => setAllInCategory(cat, true)} className="rounded border border-black/10 px-2 py-1 hover:bg-slate-50" data-testid={`automation-enable-all-${cat.toLowerCase()}`}>Enable all</button>
                      <button onClick={() => setAllInCategory(cat, false)} className="rounded border border-black/10 px-2 py-1 hover:bg-slate-50" data-testid={`automation-disable-all-${cat.toLowerCase()}`}>Disable all</button>
                    </div>
                  </div>
                  <div className="divide-y divide-black/5 overflow-hidden rounded-lg border border-black/10 bg-white">
                    {rows.map((r) => (
                      <div key={r.id} className="flex items-center justify-between gap-4 p-4">
                        <div className="min-w-0">
                          <p className="text-sm font-semibold text-ek-ink">{r.label}</p>
                          <p className="mt-0.5 text-xs text-ek-ink/60">
                            <span className="font-mono">{r.id}</span> · {r.cadence} · to <strong>{r.recipient}</strong>
                          </p>
                        </div>
                        <Toggle
                          checked={!!jobs[r.id]}
                          disabled={!master}
                          onChange={(v) => setJob(r.id, v)}
                          testid={`automation-job-${r.id}`}
                        />
                      </div>
                    ))}
                  </div>
                </section>
              ))}
            </div>

            {error && <p className="mt-4 text-sm text-rose-700" data-testid="automation-error">{error}</p>}
            {saved && <p className="mt-4 text-sm text-emerald-700" data-testid="automation-saved">Saved. Reloaded fresh state.</p>}

            <div className="mt-8 flex items-center justify-end gap-3">
              <button onClick={load} className="rounded-md border border-black/10 px-4 py-2 text-sm text-ek-ink/70 hover:bg-slate-50" data-testid="automation-reload">
                Discard changes
              </button>
              <button onClick={save} disabled={saving || !dirty} className="ek-btn inline-flex items-center gap-2 px-5 py-2 text-sm font-semibold disabled:opacity-60" data-testid="automation-save">
                {saving ? <><Loader2 size={15} className="animate-spin" /> Saving…</> : <><Save size={15} /> Save</>}
              </button>
            </div>
          </>
        )}
      </main>
    </>
  );
}

function Toggle({ checked, onChange, disabled, testid }) {
  return (
    <button
      role="switch"
      aria-checked={checked}
      aria-disabled={disabled}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors ${checked ? "bg-ek-purple" : "bg-slate-300"} ${disabled ? "opacity-40 cursor-not-allowed" : ""}`}
      data-testid={testid}
    >
      <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${checked ? "translate-x-6" : "translate-x-1"}`} />
      <Power size={10} className={`absolute left-1.5 text-white transition-opacity ${checked ? "opacity-100" : "opacity-0"}`} />
    </button>
  );
}
